import {
    getSnapshotRecord,
    putSnapshotRecord,
    SnapshotEntity,
} from 'storage/snapshotStore';
import { apiBaseUrl } from '../config';
import { sessionId } from './session';
import { authenticationService } from '_legacy/auth/AuthenticationService';
import logger from '_legacy/diagnostics/logging/logger';

interface FetchSnapshotParams {
    endpoint: string;
    entity: SnapshotEntity;
    datasetId: number;
    numberOfYears: number;
}

// Session-scoped in-memory cache: composite key → parsed records array.
// Avoids re-parsing NDJSON from IndexedDB on every dataset navigation
// when the server returns 304 Not Modified (ETag unchanged).
// Keyed by "entity-datasetId-numberOfYears:etag" to prevent cross-contamination
// between different dataset configurations that could share an ETag.
const parsedSnapshotCache = new Map<string, Record<string, unknown>[]>();

function makeParsedCacheKey(
    entity: string,
    datasetId: number,
    numberOfYears: number,
    etag: string
): string {
    return `${entity}-${datasetId}-${numberOfYears}:${etag}`;
}

// Reads a Blob as parsed NDJSON records.
// Uses blob.stream() for large blobs in modern browsers to avoid materialising
// the entire content as a string. Falls back to blob.text() for environments
// without stream() support (e.g. jsdom in tests).
async function readBlobNdjson(blob: Blob): Promise<Record<string, unknown>[]> {
    if (
        typeof blob.stream === 'function' &&
        typeof ReadableStream !== 'undefined'
    ) {
        const reader = blob
            .stream()
            .getReader() as ReadableStreamDefaultReader<Uint8Array>;
        const decoder = new TextDecoder('utf-8');
        const results: Record<string, unknown>[] = [];
        let buffer = '';
        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop()!;
                for (const line of lines) {
                    if (line.trim() === '') continue;
                    results.push(JSON.parse(line));
                }
            }
            buffer += decoder.decode();
            if (buffer.trim() !== '') results.push(JSON.parse(buffer));
        } finally {
            reader.cancel();
        }
        return results;
    }
    // Fallback: blob.text() — safe for small blobs (tests); may fail for multi-GB content.
    const text = await blob.text();
    return text
        .split('\n')
        .filter((line) => line.trim() !== '')
        .map((line) => JSON.parse(line));
}

async function fetchSnapshotWithCache({
    endpoint,
    entity,
    datasetId,
    numberOfYears,
}: FetchSnapshotParams): Promise<Record<string, unknown>[]> {
    const cachedRecord = await getSnapshotRecord({
        entity,
        datasetId,
        numberOfYears,
    });

    const cachedBlob = cachedRecord?.blob ?? null;
    const cachedEtag = cachedRecord?.etag ?? null;

    const headers: Record<string, string> = {
        authorization: encodeURIComponent(
            authenticationService.getAccessToken()
        ),
        fortSessionId: sessionId,
    };

    if (cachedEtag) {
        headers['if-none-match'] = cachedEtag;
    }

    let result: Response;
    try {
        result = await fetch(
            `https://${apiBaseUrl}/${endpoint}/Snapshot?` +
                new URLSearchParams({
                    datasetId: String(datasetId),
                    numberOfYears: String(numberOfYears),
                }),
            { headers }
        );
    } catch (error) {
        logger.error(
            `Snapshot fetch failed for ${endpoint} (datasetId=${datasetId}, numberOfYears=${numberOfYears})`,
            { endpoint, datasetId, numberOfYears }
        );
        throw error;
    }

    if (result.status === 304 && cachedBlob !== null) {
        // Fast path: if we already parsed this config+ETag in this session, return cached array
        if (cachedEtag) {
            const cacheKey = makeParsedCacheKey(
                entity,
                datasetId,
                numberOfYears,
                cachedEtag
            );
            if (parsedSnapshotCache.has(cacheKey)) {
                return parsedSnapshotCache.get(cacheKey)!;
            }
            const parsed = await readBlobNdjson(cachedBlob);
            parsedSnapshotCache.set(cacheKey, parsed);
            return parsed;
        }
        return readBlobNdjson(cachedBlob);
    }

    if (result.status === 204) {
        return [];
    }

    if (!result.ok) {
        const errorMessage = `Snapshot request failed: ${result.status} ${result.statusText} for ${endpoint} (datasetId=${datasetId}, numberOfYears=${numberOfYears})`;
        logger.error(errorMessage, {
            endpoint,
            datasetId,
            numberOfYears,
            status: result.status,
        });
        throw new Error(errorMessage);
    }

    let results: Record<string, unknown>[] = [];
    const chunks: Uint8Array[] = [];
    const reader = result.body!.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            chunks.push(value);
            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop()!;

            for (const line of lines) {
                if (line.trim() === '') continue;
                results.push(JSON.parse(line));
            }
        }

        buffer += decoder.decode();
        if (buffer.trim() !== '') results.push(JSON.parse(buffer));
    } finally {
        reader.cancel();
    }

    const blob = new Blob(chunks);

    const etag = result.headers.get('ETag');
    if (etag) {
        const cacheKey = makeParsedCacheKey(
            entity,
            datasetId,
            numberOfYears,
            etag
        );
        parsedSnapshotCache.set(cacheKey, results);
        putSnapshotRecord({
            entity,
            datasetId,
            numberOfYears,
            blob,
            etag,
        }).catch(() => {
            logger.warn(
                `Failed to store snapshot in IndexedDB for ${endpoint} (datasetId=${datasetId}, numberOfYears=${numberOfYears})`
            );
        });
    } else {
        logger.warn(
            `No ETag header in snapshot response for ${endpoint} (datasetId=${datasetId})`
        );
    }

    return results;
}

export const snapshotApi = {
    fetchFixturesSnapshot: async ({
        datasetId,
        numberOfYears,
    }: {
        datasetId: number;
        numberOfYears: number;
    }): Promise<Record<string, unknown>[]> =>
        await fetchSnapshotWithCache({
            endpoint: 'Fixtures',
            entity: 'fixtures',
            datasetId,
            numberOfYears,
        }),

    fetchOrdersSnapshot: async ({
        datasetId,
        numberOfYears,
    }: {
        datasetId: number;
        numberOfYears: number;
    }): Promise<Record<string, unknown>[]> =>
        await fetchSnapshotWithCache({
            endpoint: 'Orders',
            entity: 'orders',
            datasetId,
            numberOfYears,
        }),
};
